import React, { useState, useRef, useEffect } from "react";
import DropDownIcon from "../../Assets/Images/CreateBatch/icon-dropdown-menu.png";


export default function MultiSelectedWithDiabled({
    width,
    label,
    selected = [],
    setSelected,
    options = [],
    customWidth,
    customTop,
    isRequired,
    style,
    className,
    showSelectAll,
    onDropdownClose,
    disabled = false,
    autoSelectAll = false, 
    placeholder = "Select", 
}) {
    const [isActive, setIsActive] = useState(false);
    const dropdownRef = useRef(null);
    const hasAutoSelected = useRef(false); // Track if auto-select has happened

    // Helper: get IDs of options that are selectable (not disabled)
    const selectableOptionIds = () => {
      return (options || [])
        .filter(opt => !opt.disabled)
        .map(opt => opt.id);
    };

    // Auto-select all options when options are first loaded (only once)
    useEffect(() => {
        const selectable = selectableOptionIds();
        if (autoSelectAll && selectable.length > 0 && Array.isArray(selected) && selected.length === 0 && !hasAutoSelected.current) {
            setSelected(selectable);
            hasAutoSelected.current = true;
        }
    }, [options, autoSelectAll, selected, setSelected]);

    // Reset the auto-select flag when options change (e.g., course change)
    useEffect(() => {
        if (options.length === 0) {
            hasAutoSelected.current = false;
        }
    }, [options]);

    useEffect(() => {
        function handleClickOutside(event) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsActive(false);
                if (onDropdownClose) onDropdownClose();
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [dropdownRef, onDropdownClose]);

    // Guarded toggle for one option (ignores disabled options)
    const handleClick = (option) => {
        if (option.disabled) return; // nothing to do for disabled options
        const selArray = Array.isArray(selected) ? selected : [];
        const isSelected = selArray.some(id => String(id) === String(option.id));
        
        if (isSelected) {
            setSelected(selArray.filter((id) => String(id) !== String(option.id)));
        } else {
            setSelected([...selArray, option.id]);
        }
    };

    // Select all / Unselect all but only for selectable options
    const handleSelectAll = () => {
        const selArray = Array.isArray(selected) ? selected : [];
        const allIds = selectableOptionIds();
        const areAllSelected = allIds.length > 0 && allIds.every(id => selArray.some(s => String(s) === String(id)));

        if (areAllSelected) {
            // Unselect only the selectable ones; keep any (if present) disabled ones out of selected
            setSelected([]);
        } else {
            setSelected(allIds);
        }
    };

    const selectedLabels = () => {
        const selArray = Array.isArray(selected) ? selected : [];
        if (selArray.length === 0) return "";
        return (options || [])
            .filter((option) => selArray.some(id => String(id) === String(option.id)))
            .map((option) => option.label)
            .join(", ");
    };

    // Compute select-all checked/indeterminate states based on selectable options
    const selectableIds = selectableOptionIds();
    const selArray = Array.isArray(selected) ? selected : [];
    const selectableSelectedCount = selectableIds.filter(id => selArray.some(s => String(s) === String(id))).length;
    const isAllSelectableSelected = selectableIds.length > 0 && selectableSelectedCount === selectableIds.length;
    const isSomeSelectableSelected = selectableSelectedCount > 0 && selectableSelectedCount < selectableIds.length;

    return (
        <div
            className={`dropdown-CB ${className || ""}`}
            style={{ width, ...style, opacity: disabled ? 0.6 : 1, pointerEvents: disabled ? "none" : "auto", position: "relative" }}
        >
            <label className="dropdown-CB-label">
                {label}
                {isRequired && <span>*</span>}
            </label>
            <div
                className="dropdown-btn"
                onClick={() => { if (!disabled) setIsActive(!isActive); }}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); if (!disabled) setIsActive(!isActive); } }}
                aria-expanded={isActive}
            >
                <div
                    className="selected-options"
                    style={{
                        maxWidth: "200px",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        color: (Array.isArray(selected) && selected.length === 0) ? "grey" : "#6A6A6A",
                    }}
                >
                    {Array.isArray(selected) && selected.length > 0
                        ? selectedLabels()
                        : placeholder}
                </div>
                <img src={DropDownIcon} alt="dropdown-icon" />
            </div>

            {isActive && (
                <div
                    className="dropdown-content-CBV2"
                    style={{ width: customWidth, top: customTop }}
                    ref={dropdownRef}
                >
                    {showSelectAll && options.length > 0 && (
                        <div className="dropdown-item-CBV2" style={{display: "flex", alignItems: 'center', padding: '8px 10px'}}>
                            <input
                                type="checkbox"
                                className="delete-question-checkbox-dqc"
                                checked={isAllSelectableSelected}
                                ref={(el) => {
                                  if (el) el.indeterminate = isSomeSelectableSelected;
                                }}
                                onChange={handleSelectAll}
                                disabled={disabled || selectableIds.length === 0}
                            />
                            <label 
                                style={{ marginLeft: "8px", cursor: (disabled || selectableIds.length === 0) ? "not-allowed" : "pointer", opacity: (disabled || selectableIds.length === 0) ? 0.6 : 1 }} 
                                onClick={() => { if (!disabled && selectableIds.length > 0) handleSelectAll(); }}
                            >
                                Select All
                            </label>
                        </div>
                    )}

                    {options.map((option) => {
                        const selArrayLocal = Array.isArray(selected) ? selected : [];
                        const checked = selArrayLocal.some(id => String(id) === String(option.id));
                        const isOptionDisabled = !!option.disabled;
                        return (
                            <div key={option.id} className="dropdown-item-CBV2" style={{display: "flex", alignItems: 'center', padding: '8px 10px', opacity: isOptionDisabled ? 0.6 : 1}}>
                                <input
                                    type="checkbox"
                                    className="delete-question-checkbox-dqc"
                                    checked={checked}
                                    onChange={() => handleClick(option)}
                                    disabled={disabled || isOptionDisabled}
                                />
                                <label
                                    style={{ 
                                        marginLeft: "8px", 
                                        cursor: (disabled || isOptionDisabled) ? "not-allowed" : "pointer", 
                                        opacity: (disabled || isOptionDisabled) ? 0.8 : 1 
                                    }}
                                    onClick={() => { if (!disabled && !isOptionDisabled) handleClick(option); }}
                                >
                                    {option.label}
                                </label>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
